import moment, { Moment } from "moment";
import { isEmptyOrNull } from "./DataUtils";
import { DAYS_DROPDOWN_OPTIONS } from "../config/constants";

export const HH_MM_SS = "HH:mm:ss";
export const HH_MM = "HH:mm";

/**
 * Converts a time value (string, Date, moment, or null) to a native Date object for use in pickers.
 * @param value The value to parse
 * @returns Date object or null
 */
export function parseTimeValue(value: any): Date | null {
  if (!value) return null;

  // If it's already a Date object, return it
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value;
  }

  // If it's a moment object
  if (moment.isMoment(value)) {
    return value.toDate();
  }

  // If it's a string, parse it with moment
  if (typeof value === 'string') {
    const parsed = moment(value, [HH_MM_SS, HH_MM, 'HH:mm'], true);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  return null;
}

export function getDateFromData(dateData: any) {
  if (dateData) {
    return moment(dateData).format("DD MMM YYYY");
  }
  return dateData;
}

export function getTimeFormatData(timeData: any) {
  if (timeData) {
    return moment(timeData, HH_MM_SS).format("HH:mm");
  }
  return timeData;
}

export function getDateFromPicker(dateData: any) {
  return moment(dateData).format("YYYY-MM-DD");
}

export function convertDateFromCurrentFormatToNewFormat(dateData: any, currentFormat: any, newFormat: any) {
  let returnDate = moment(dateData, currentFormat).format(newFormat);
  return returnDate;
}

interface GetTimeOptions {
  use24HourFormat?: boolean;
  includeSeconds?: boolean;
}

export const getTimeInHourAndMinutes = (minTime: string | number, options: GetTimeOptions = {}): string => {
  const { use24HourFormat = false, includeSeconds = false } = options;

  const totalMinutes = !isNaN(Number(minTime)) ? Math.floor(Number(minTime) / 60) : 0;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const seconds = includeSeconds ? (totalMinutes * 60) % 60 : 0;

  const formattedTime = use24HourFormat
    ? `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}${includeSeconds ? `:${String(seconds).padStart(2, "0")}` : ""}`
    : `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} min${minutes !== 1 ? "s" : ""}${includeSeconds ? ` ${seconds} sec${seconds !== 1 ? "s" : ""}` : ""}`;
  return formattedTime;
};

export type TimeValue = string | number | Moment;
export enum DateFormat {
  YYYY_MM_DD = "YYYY-MM-DD",
  YYYY_MM_DDTHH_mm_ss = "YYYY-MM-DDTHH:mm:ss",
  HH_MM_SS = "HH:mm:ss",
  HH_MM = "HH:mm",
}

export const getMomentDateObj = (date: TimeValue, currentFormat: DateFormat) => {
  if (!isEmptyOrNull(date) && moment(date, currentFormat).isValid()) {
    return moment(date, currentFormat);
  }
  return date;
};

export const isDateValid = (date: TimeValue, currentFormat?: DateFormat) => {
  if (!isEmptyOrNull(currentFormat)) {
    return moment(date, currentFormat).isValid();
  }
  return moment(date).isValid();
};

export const getMomentDateBasedOnFormat = (date: TimeValue, currentFormat: DateFormat) => {
  if (!isEmptyOrNull(date) && moment(date).isValid()) {
    return moment(date).format(currentFormat);
  }
  return moment(date);
};

export const getMomentDateWithCurrentFormatBasedOnFormat = (date: TimeValue, currentFormat: DateFormat, returnFormat: DateFormat) => {
  if (!isEmptyOrNull(date) && moment(date).isValid()) {
    return moment(date, currentFormat).format(returnFormat);
  }
  return moment(date);
};

export function getTimeValueAsNumber(timeValue: TimeValue): number {
  if (typeof timeValue === "number") {
    return timeValue; // Already a number
  } else if (typeof timeValue === "string") {
    // Attempt to parse the string as a date/time
    const momentTime = moment(timeValue);
    if (momentTime.isValid()) {
      return momentTime.valueOf();
    } else {
      // Handle invalid string format (e.g., throw an error or return a default value)
      console.error(`Invalid time string: ${timeValue}`);
      return 0; // Or throw an error
    }
  } else if (moment.isMoment(timeValue)) {
    return timeValue.valueOf(); // Moment object
  } else {
    // Handle unexpected type (e.g., throw an error)
    console.error(`Unexpected time value type: ${timeValue}`);
    return 0; // Or throw an error
  }
}

export const getMomentEndTimeBasedOnFormat = (date: TimeValue, currentFormat: DateFormat) => {
  if (!isEmptyOrNull(date) && moment(date).isValid()) {
    const momentDate = moment(date);
    if (momentDate.hours() === 23 && momentDate.minutes() === 59) {
      momentDate.seconds(59);
    }
    return momentDate.format(currentFormat);
  }
  return moment();
};

export const getTodaysDayInString = (): string => {
  const todayIndex = moment().day();
  const correctedIndex = (todayIndex + 6) % 7;
  return DAYS_DROPDOWN_OPTIONS[correctedIndex]?.id || DAYS_DROPDOWN_OPTIONS[0].id;
};
